import { FC, useState, useRef, useEffect, useMemo } from 'react'
import Button from 'react-bootstrap/Button'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import { InfoField } from 'types/infoField'
import {
  setTitle,
  setErrorMessage,
  getActiveDiscInstUrlInfo
} from 'reducers/activecourse/activecourseSlice'
import {
  CourseMethodType,
  CourseOrderItem,
  CourseTabTitle,
  getCourseStatusName
} from 'share/domain/course'
import CourseViewInstructionsModal from 'components/features/dashboard/course/CourseViewInstructionsModal'
import { CoursesContainer, TwoBtnsContainer } from 'components/atoms/Layout'
import InfoFields from 'components/atoms/InfoFields'

import { CoursesOnlineBtnsContainer, CoursesPhoneBtnContainer } from './styles'

interface CourseTabFinishProps {
  currentTab: string
  course: CourseOrderItem
}

const CourseTabFinish: FC<CourseTabFinishProps> = ({ currentTab, course }) => {
  const dispatch = useAppDispatch()
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const discInstUrlInfo = useAppSelector(getActiveDiscInstUrlInfo)
  const [showCourseInstructionsModal, setShowCourseInstructionsModal] =
    useState(false)
  const courseUrl = useRef('')

  useEffect(() => {
    let title = ''
    if (activeCourse?.activeTab) {
      title =
        activeCourse.activeTab === 'first'
          ? CourseTabTitle.TAKE_FIRST_COURSE
          : CourseTabTitle.TAKE_SECOND_COURSE
    }

    dispatch(setTitle(title))
    dispatch(setErrorMessage(null))
  }, [activeCourse?.activeTab])

  useEffect(() => {
    if (discInstUrlInfo) {
      courseUrl.current = discInstUrlInfo.courseUrl
    }
  }, [discInstUrlInfo])

  const fields = useMemo<InfoField[]>(() => {
    if (course.courseMethod === CourseMethodType.Phone) {
      return [
        {
          content: (
            <a href={`tel:${course.coursePhoneNumber}`}>
              {course.coursePhoneNumber}
            </a>
          ),
          key: 'coursePhoneNumber',
          title: 'Course Phone Number'
        },
        {
          content: course.courseAccessCode,
          key: 'courseAccessCode',
          title: 'Access Code'
        },
        {
          content: getCourseStatusName(course.courseStatus, true),
          key: 'courseStatus',
          title: 'Course Status'
        },
        {
          content: course.providerName,
          key: 'courseProvider',
          title: 'Course Provider'
        },
        {
          child: (
            <CoursesPhoneBtnContainer>
              <Button
                variant='outline-secondary'
                onClick={viewInstructionsClicked}
              >
                View Instructions
              </Button>
            </CoursesPhoneBtnContainer>
          ),
          key: 'coursePhoneBtn'
        }
      ]
    }

    return [
      {
        child: (
          <CoursesOnlineBtnsContainer data-testid='course-tab-finish-content'>
            <TwoBtnsContainer>
              <Button
                variant='outline-secondary'
                onClick={viewInstructionsClicked}
                data-testid='course-tab-finish-view-instructions-button'
              >
                View Instructions
              </Button>
              <Button
                onClick={() => handleFinishingCourse()}
                data-testid='course-tab-finish-course-button'
              >
                {currentTab === 'first'
                  ? 'Finish the First Course'
                  : 'Finish the Second Course'}
              </Button>
            </TwoBtnsContainer>
          </CoursesOnlineBtnsContainer>
        ),
        key: 'courseOnlineBtns'
      },
      {
        content: getCourseStatusName(course.courseStatus),
        key: 'courseStatus',
        title: 'Course Status'
      },
      {
        content: course.providerName,
        key: 'courseProvider',
        title: 'Course Provider'
      }
    ]
  }, [
    course.courseAccessCode,
    course.courseMethod,
    course.coursePhoneNumber,
    course.courseStatus,
    course.providerName
  ])

  function handleFinishingCourse() {
    if (course.courseMethod === CourseMethodType.Online) {
      // open new tab with the discInstUrlInfo.courseUrl

      window.open(courseUrl.current, '_blank')
    }
  }

  function viewInstructionsClicked() {
    setShowCourseInstructionsModal(true)
  }

  function closeViewInstructionsModal() {
    setShowCourseInstructionsModal(false)
  }

  return (
    <>
      <CoursesContainer>
        <InfoFields fields={fields} />
      </CoursesContainer>
      <CourseViewInstructionsModal
        show={showCourseInstructionsModal}
        onHide={closeViewInstructionsModal}
      />
    </>
  )
}

export default CourseTabFinish
